import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    FileText,
    Cpu,
    MessageSquare,
    Send,
    Check,
    Video,
    Sparkles,
    Zap
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { handleWhatsAppClick } from '../utils/gtag';

const workflowSteps = [
    {
        id: 1,
        title: 'Berikan Brief Anda',
        description: 'Sampaikan ide atau kebutuhan umum Anda terkait video yang diinginkan.',
        icon: FileText
    },
    {
        id: 2,
        title: 'Proses Produksi',
        description: 'Pengembangan video menggunakan berbagai tools AI canggih.',
        icon: Cpu
    },
    {
        id: 3,
        title: 'Ulasan & Revisi',
        description: 'Peninjauan versi awal dan pemberian masukan penyempurnaan.',
        icon: MessageSquare
    },
    {
        id: 4,
        title: 'Pengiriman Final',
        description: 'Video akhir dikirimkan dalam format yang sesuai kebutuhan.',
        icon: Send
    },
];

const services = [
    {
        title: 'Video AI Realistis',
        description: 'Pembuatan video dengan avatar AI atau teknologi image-to-video untuk presentasi dan iklan.',
        icon: Video
    },
    {
        title: 'AI Content Creation',
        description: 'Produksi konten massal untuk TikTok dan Instagram menggunakan prompt engineering yang presisi.',
        icon: Sparkles
    },
    {
        title: 'Otomasi Konten UMKM',
        description: 'Solusi bagi pemilik bisnis yang ingin tetap eksis di media sosial tanpa harus menghabiskan waktu berjam-jam.',
        icon: Zap
    },
];

const features = [
    'Video Eksklusif (Durasi hingga 1 menit)',
    'Character Konsisten AI',
    'Free Voice Over & Free Backsound Musik',
    'Editing Profesional & Hak Milik Penuh',
    'Human Touch Quality (E-E-A-T)',
    'Biaya Efisien untuk UMKM',
];

const sliderImages = [
    { src: '/jasa-ai-content.png', alt: 'Jasa Pembuatan Konten AI Kreatif - Thumbnail' },
    { src: '/ai-content-showcase-1.png', alt: 'AI Content Showcase 1' },
    { src: '/ai-content-showcase-2.png', alt: 'AI Content Showcase 2' },
    { src: '/ai-content-showcase-3.png', alt: 'AI Content Showcase 3' },
    { src: '/ai-content-showcase-5.png', alt: 'AI Content Showcase 5' },
    { src: '/ai-content-showcase-6.png', alt: 'AI Content Showcase 6' },
    { src: '/ai-content-showcase-7.png', alt: 'AI Content Showcase 7' },
    { src: '/ai-content-showcase-8.png', alt: 'AI Content Showcase 8' },
    { src: '/ai-content-showcase-4.png', alt: 'AI Content Showcase 4' },
];

export function AIContentServicePage() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideDirection, setSlideDirection] = useState(0);

    const goToPrevSlide = () => {
        setSlideDirection(-1);
        setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
    };

    const goToNextSlide = () => {
        setSlideDirection(1);
        setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
    };

    const goToSlide = (index: number) => {
        setSlideDirection(index > currentSlide ? 1 : -1);
        setCurrentSlide(index);
    };

    return (
        <div className="min-h-screen text-white overflow-x-hidden">
            <Helmet>
                <title>Jasa Pembuatan Konten AI Kreatif – Solusi Cerdas Bisnis Masa Depan | Ikhsanztech</title>
                <meta name="description" content="Tingkatkan efisiensi bisnis dengan jasa pembuatan konten AI. Mulai dari video AI realistis, script writer, hingga konten media sosial otomatis." />
                <meta name="keywords" content="Jasa Pembuatan Konten AI, AI Content Creation, Video AI, Konten AI Indonesia, UMKM Digital, Otomasi Konten" />
            </Helmet>
            <div className="global-fixed-bg" />
            <div className="relative z-10">
                <Header />

                {/* Fixed Back Button */}
                <Link
                    to="/jasa-kami"
                    className="fixed top-[72px] sm:top-20 left-4 sm:left-6 z-50 inline-flex items-center gap-1.5 sm:gap-2 text-white/90 text-xs sm:text-sm font-medium hover:text-white transition-colors bg-black/60 backdrop-blur-sm px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full border border-white/10 hover:bg-black/80"
                >
                    <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Kembali</span>
                </Link>

                <section className="pt-32 sm:pt-28 pb-16 px-4 sm:px-6">
                    <div className="max-w-5xl mx-auto">
                        {/* Header with Image */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-12 sm:mb-16"
                        >
                            <div className="flex flex-col lg:flex-row gap-8 items-center">
                                {/* Service Image Slider */}
                                <div className="w-full max-w-[320px] sm:max-w-md lg:max-w-none lg:w-1/2 mx-auto lg:mx-0">
                                    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/40">
                                        {/* Slider Container */}
                                        <div className="relative aspect-square overflow-hidden">
                                            <AnimatePresence initial={false} custom={slideDirection} mode="wait">
                                                <motion.img
                                                    key={currentSlide}
                                                    src={sliderImages[currentSlide].src}
                                                    alt={sliderImages[currentSlide].alt}
                                                    custom={slideDirection}
                                                    initial={{ opacity: 0, x: slideDirection > 0 ? 100 : -100 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: slideDirection > 0 ? -100 : 100 }}
                                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                    className="absolute inset-0 w-full h-full object-contain"
                                                />
                                            </AnimatePresence>
                                        </div>

                                        {/* Navigation Arrows */}
                                        <button
                                            onClick={goToPrevSlide}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/70 transition-all duration-300 z-10 group"
                                            aria-label="Previous image"
                                        >
                                            <ChevronLeft className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
                                        </button>
                                        <button
                                            onClick={goToNextSlide}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/70 transition-all duration-300 z-10 group"
                                            aria-label="Next image"
                                        >
                                            <ChevronRight className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
                                        </button>

                                        {/* Dot Indicators */}
                                        <div className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-1.5 z-10 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5">
                                            {sliderImages.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => goToSlide(index)}
                                                    className={`rounded-full transition-all duration-300 ${
                                                        index === currentSlide
                                                            ? 'bg-white w-5 h-2'
                                                            : 'bg-white/40 hover:bg-white/60 w-2 h-2'
                                                    }`}
                                                    aria-label={`Go to slide ${index + 1}`}
                                                />
                                            ))}
                                        </div>

                                        {/* Slide Counter */}
                                        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1 text-white/80 text-xs font-medium z-10">
                                            {currentSlide + 1} / {sliderImages.length}
                                        </div>
                                    </div>
                                </div>

                                {/* Title & Package Info */}
                                <div className="w-full lg:w-1/2 text-center lg:text-left">
                                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                                        Jasa Pembuatan Konten AI Kreatif – Solusi Cerdas Bisnis Masa Depan
                                    </h1>
                                    <div className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-xl p-6">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                            <div>
                                                <p className="text-white font-bold text-lg">Waktu Pengerjaan</p>
                                                <p className="text-white/60">1 Hari</p>
                                            </div>
                                            <div className="sm:text-right">
                                                <p className="text-white font-bold text-lg">Jumlah Revisi</p>
                                                <p className="text-white/60">Tak Terbatas</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Description Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="mb-12 sm:mb-16"
                        >
                            <div className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8">
                                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-6">
                                    Selamat datang di <strong className="text-white">masa depan pemasaran digital</strong>. Ikhsanztech Studio memadukan kreativitas manusia dengan kecanggihan kecerdasan buatan melalui layanan <strong className="text-white">jasa pembuatan konten AI</strong>. Kami membantu UMKM di Indonesia bertransformasi ke era digital dengan biaya yang lebih efisien namun hasil tetap berkualitas premium.
                                </p>
                                <p className="text-white/80 text-base sm:text-lg leading-relaxed">
                                    Kami memastikan setiap konten AI yang kami hasilkan tetap memiliki <strong className="text-white">'sentuhan manusia' (human touch)</strong> agar tetap relevan dan dipercaya oleh audiens Anda (E-E-A-T). Jadilah <strong className="text-white">pionir di industri Anda</strong> dengan memanfaatkan teknologi AI bersama spesialis yang tepat.
                                </p>
                            </div>
                        </motion.div>

                        {/* Services We Offer */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="mb-12 sm:mb-16"
                        >
                            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">
                                Layanan Unggulan AI Kami
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {services.map((service, index) => {
                                    const Icon = service.icon;
                                    return (
                                        <motion.div
                                            key={service.title}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 + index * 0.1 }}
                                            className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-xl p-6"
                                        >
                                            <div className="flex flex-col items-center text-center">
                                                <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center mb-4">
                                                    <Icon className="w-7 h-7 text-white" />
                                                </div>
                                                <h3 className="text-white font-bold text-base mb-2">{service.title}</h3>
                                                <p className="text-white/60 text-sm">{service.description}</p>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>

                        {/* Workflow Steps */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25 }}
                            className="mb-12 sm:mb-16"
                        >
                            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">
                                Alur Kerja
                            </h2>

                            <div className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {workflowSteps.map((step, index) => {
                                        const Icon = step.icon;
                                        return (
                                            <motion.div
                                                key={step.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.25 + index * 0.1 }}
                                                className="flex items-start gap-4 p-4 rounded-xl border border-white/10"
                                            >
                                                <div className="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center">
                                                    <Icon className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <span className="text-white/50 text-xs font-medium">Step {step.id}</span>
                                                    <p className="text-white text-sm font-bold mb-1">{step.title}</p>
                                                    <p className="text-white/60 text-xs">{step.description}</p>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.div>

                        {/* Features Checklist */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35 }}
                            className="mb-12 sm:mb-16"
                        >
                            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">
                                Fitur Termasuk
                            </h2>

                            <div className="bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {features.map((feature, index) => (
                                        <motion.div
                                            key={feature}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.35 + index * 0.05 }}
                                            className="flex items-center gap-3 p-3 rounded-xl border border-white/10"
                                        >
                                            <div className="flex-shrink-0 w-6 h-6 rounded-full border border-white/20 flex items-center justify-center">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-white text-sm font-medium">{feature}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="text-center"
                        >
                            <p className="text-white/60 text-sm sm:text-base mb-4">
                                Tertarik? Langsung Chat ya
                            </p>
                            <motion.button
                                onClick={() => handleWhatsAppClick('https://wa.me/6287877066270?text=Halo%20Ikhsanztech-studio%2C%20saya%20tertarik%20dengan%20Jasa%20Pembuatan%20Konten%20AI.%20Mari%20kita%20diskusikan%20kebutuhan%20bisnis%20saya!')}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 bg-white hover:bg-white/90 text-gray-900 font-semibold px-8 py-4 rounded-full transition-all duration-300"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                                Hubungi via WhatsApp
                            </motion.button>
                        </motion.div>
                    </div>
                </section>

                <Footer />
            </div>
        </div>
    );
}
